/**
 * `/settings` (docs/design/06 §5; 02 §2.1): default rules tier, tier-2 automation toggles (owner decision
 * Q5 defaults), priority behaviour (Q25: auto-pass, hold priority), animation speed and reduced motion, the practice
 * bot's speed and the "your turn" alerts, and the sound mixer (batch 3 #23: master, attention and game feedback
 * levels, mute, a "Test sound" button that starts audio from the click, and a note when the browser blocks it).
 * Saved to `play.profile` when signed in (and cached on this device).
 */
import { ActionButton as Button, CountPill, Field, Frame, LOOK, PageHead, Segmented, Switch } from '@play/ui';
import { Bell, Check, Gauge, Hand, Loader2, Settings as SettingsIcon, Swords, Volume2, Wand2 } from 'lucide-react';
import { type ReactNode, useState, useSyncExternalStore } from 'react';
import { useAuth } from '../auth/auth';
import { TIER_INFO } from '../features/play/TierPicker';
import { audioBlocked, channelGain, playSound, subscribeAudio, unlockAudio } from '../lib/audio';
import { ENABLED_TIERS } from '../lib/env';
import type {
  AnimationSpeed,
  BotSpeedSetting,
  TouchModeSetting,
  PlaySettings,
  ReducedMotionSetting,
  SoundSettings,
} from '../settings/schema';
import { useSettings } from '../settings/settings-store';

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-4 py-3 cursor-pointer">
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium" style={{ color: LOOK.text }}>
          {label}
        </span>
        {hint ? (
          <span className="block text-[11.5px] leading-4 mt-0.5" style={{ color: LOOK.muted }}>
            {hint}
          </span>
        ) : null}
      </span>
      <Switch checked={checked} onChange={onChange} />
    </label>
  );
}

function Choice<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const byKey = new Map(options.map((o) => [String(o.value), o.value]));
  return (
    <Field label={label} className="py-3">
      <Segmented
        label={label}
        value={String(value)}
        onChange={(k) => {
          const v = byKey.get(k);
          if (v !== undefined) onChange(v);
        }}
        options={options.map((o) => [String(o.value), o.label] as const)}
      />
    </Field>
  );
}

function Section({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Frame
      title={title}
      icon={icon}
      end={
        <span className="text-[12.5px] hidden sm:inline truncate" style={{ color: LOOK.muted }}>
          {subtitle}
        </span>
      }
    >
      <div className="px-4 py-1 divide-y divide-white/[0.06]">{children}</div>
    </Frame>
  );
}

/** A 0-100% level slider with its label. */
function Level({
  label,
  hint,
  value,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  const pct = Math.round(value * 100);
  return (
    <label className={`flex items-center gap-4 py-3 ${disabled ? 'opacity-50' : ''}`}>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium" style={{ color: LOOK.text }}>
          {label}
        </span>
        {hint ? (
          <span className="block text-[11.5px] leading-4 mt-0.5" style={{ color: LOOK.muted }}>
            {hint}
          </span>
        ) : null}
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={pct}
        disabled={disabled}
        aria-label={label}
        aria-valuetext={`${String(pct)}%`}
        className="w-32 sm:w-40 accent-[#b9adff]"
        onChange={(e) => {
          onChange(Number(e.target.value) / 100);
        }}
      />
      <span className="w-10 text-right tabular-nums text-[12px]" style={{ color: LOOK.sub }}>
        {pct}%
      </span>
    </label>
  );
}

/** The sound mixer: mute, master, the attention chime and game feedback, a test button. */
function SoundSection({ sound, onChange }: { sound: SoundSettings; onChange: (s: SoundSettings) => void }) {
  const blocked = useSyncExternalStore(subscribeAudio, audioBlocked, () => false);
  const [tested, setTested] = useState<'idle' | 'played' | 'silent'>('idle');
  const patch = (p: Partial<SoundSettings>) => {
    onChange({ ...sound, ...p });
  };
  const test = () => {
    // Called from the click: the user gesture lets the browser start audio.
    void unlockAudio().then((ok) => {
      if (!ok) {
        setTested('silent');
        return;
      }
      const attention = channelGain(sound, 'attention');
      const feedback = channelGain(sound, 'feedback');
      if (attention > 0) playSound('chime', attention);
      if (feedback > 0)
        setTimeout(
          () => {
            playSound('play', feedback);
          },
          attention > 0 ? 450 : 0,
        );
      setTested(attention > 0 || feedback > 0 ? 'played' : 'silent');
    });
  };
  const off = sound.muted;
  return (
    <Section title="Sound" subtitle="Soft tones, no music" icon={<Volume2 size={15} color="#b9adff" />}>
      <Toggle
        label="Mute all sounds"
        checked={sound.muted}
        onChange={(v) => {
          patch({ muted: v });
        }}
      />
      <Level
        label="Master volume"
        value={sound.master}
        disabled={off}
        onChange={(v) => {
          patch({ master: v });
        }}
      />
      <Toggle
        label="Attention chime"
        hint="When the turn, a decision, your response or your showdown focus comes to you."
        checked={sound.attention.on}
        onChange={(v) => {
          patch({ attention: { ...sound.attention, on: v } });
        }}
      />
      <Level
        label="Attention volume"
        value={sound.attention.volume}
        disabled={off || !sound.attention.on}
        onChange={(v) => {
          patch({ attention: { ...sound.attention, volume: v } });
        }}
      />
      <Toggle
        label="Game feedback"
        hint="Soft clicks for draws, plays, scores and damage."
        checked={sound.feedback.on}
        onChange={(v) => {
          patch({ feedback: { ...sound.feedback, on: v } });
        }}
      />
      <Level
        label="Feedback volume"
        value={sound.feedback.volume}
        disabled={off || !sound.feedback.on}
        onChange={(v) => {
          patch({ feedback: { ...sound.feedback, volume: v } });
        }}
      />
      <div className="flex flex-wrap items-center gap-3 py-3">
        <Button size="sm" variant="secondary" onClick={test} data-testid="sound-test">
          Test sound
        </Button>
        <span className="text-[12px] min-w-0 flex-1" role="status" style={{ color: blocked ? LOOK.warn : LOOK.muted }}>
          {blocked || tested === 'silent'
            ? blocked
              ? 'Your browser blocked sound on this page. Press Test sound (or click anywhere on the board) to allow it.'
              : 'Nothing to play: sounds are muted or switched off.'
            : tested === 'played'
              ? 'Played the sounds that are on.'
              : null}
        </span>
      </div>
    </Section>
  );
}

/** The settings page. */
export function SettingsPage() {
  const { user } = useAuth();
  const profile = useSettings((s) => s.profile);
  const sync = useSettings((s) => s.sync);
  const update = useSettings((s) => s.update);
  const set = (patch: Partial<PlaySettings>) => {
    update((p) => ({ ...p, settings: { ...p.settings, ...patch } }));
  };
  const auto = (key: keyof PlaySettings['automation'], v: boolean) => {
    update((p) => ({ ...p, settings: { ...p.settings, automation: { ...p.settings.automation, [key]: v } } }));
  };
  const a = profile.settings.automation;
  return (
    <div className="flex flex-col">
      <PageHead
        title="Settings"
        icon={<SettingsIcon size={18} />}
        end={
          <CountPill tone={sync === 'error' ? LOOK.bad : LOOK.sub}>
            <span className="inline-flex items-center gap-1.5" role="status">
              {sync === 'saving' || sync === 'loading' ? (
                <Loader2 size={13} className="animate-spin motion-reduce:animate-none" />
              ) : sync === 'ready' ? (
                <Check size={13} />
              ) : null}
              {sync === 'saving'
                ? 'Saving'
                : sync === 'loading'
                  ? 'Loading'
                  : sync === 'ready'
                    ? 'Saved'
                    : sync === 'error'
                      ? 'Couldn’t save'
                      : 'On this device'}
            </span>
          </CountPill>
        }
      >
        {user ? 'Saved to your Runebound account.' : 'Saved on this device. Sign in to keep them with your account.'}
      </PageHead>
      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <div className="flex flex-col gap-4">
          <Section
            title="Gameplay"
            subtitle="Your default when you queue or open a room"
            icon={<Swords size={15} color="#b9adff" />}
          >
            <Choice
              label="Default rules tier"
              value={profile.defaultTier === 3 ? 2 : profile.defaultTier}
              options={ENABLED_TIERS.map((t) => ({ value: t, label: `Tier ${t} · ${TIER_INFO[t].name}` }))}
              onChange={(t) => {
                update((p) => ({ ...p, defaultTier: t }));
              }}
            />
          </Section>
          <Section title="Priority" subtitle="How priority windows reach you" icon={<Hand size={15} color="#b9adff" />}>
            <Toggle
              label="Auto-pass empty priority windows"
              hint="Only when you hold no hidden cards, so your timing never gives anything away."
              checked={profile.settings.autoPass}
              onChange={(v) => {
                set({ autoPass: v });
              }}
            />
            <Toggle
              label="Hold priority after your own plays"
              checked={profile.settings.holdPriority}
              onChange={(v) => {
                set({ holdPriority: v });
              }}
            />
          </Section>
          <Section title="Motion" subtitle="Animations on the board" icon={<Gauge size={15} color="#b9adff" />}>
            <Choice<AnimationSpeed>
              label="Animation speed"
              value={profile.settings.animationSpeed}
              options={[
                { value: 'slow', label: 'Slow' },
                { value: 'normal', label: 'Normal' },
                { value: 'fast', label: 'Fast' },
              ]}
              onChange={(v) => {
                set({ animationSpeed: v });
              }}
            />
            <Choice<ReducedMotionSetting>
              label="Reduce motion"
              value={profile.settings.reducedMotion}
              options={[
                { value: 'system', label: 'System' },
                { value: 'on', label: 'On' },
                { value: 'off', label: 'Off' },
              ]}
              onChange={(v) => {
                set({ reducedMotion: v });
              }}
            />
            <Choice<TouchModeSetting>
              label="Touch: tap to inspect"
              value={profile.settings.touchMode}
              options={[
                { value: 'auto', label: 'Auto' },
                { value: 'on', label: 'On' },
                { value: 'off', label: 'Off' },
              ]}
              onChange={(v) => {
                set({ touchMode: v });
              }}
            />
          </Section>
          <Section
            title="Practice & alerts"
            subtitle="The bot's pace and your turn"
            icon={<Bell size={15} color="#b9adff" />}
          >
            <Choice<BotSpeedSetting>
              label="Practice bot speed"
              value={profile.settings.botSpeed}
              options={[
                { value: 'slow', label: 'Slow' },
                { value: 'normal', label: 'Normal' },
                { value: 'fast', label: 'Fast' },
                { value: 'instant', label: 'Instant' },
              ]}
              onChange={(v) => {
                set({ botSpeed: v });
              }}
            />
            <Toggle
              label="Show “Your turn” in the tab title"
              hint="When the turn or a decision comes to you while you're in another tab."
              checked={profile.settings.titleAlert}
              onChange={(v) => {
                set({ titleAlert: v });
              }}
            />
          </Section>
          <SoundSection
            sound={profile.settings.sound}
            onChange={(sound) => {
              set({ sound });
            }}
          />
        </div>
        <Section
          title="Tier 2 automation"
          subtitle="Each toggle affects only your own bookkeeping"
          icon={<Wand2 size={15} color="#b9adff" />}
        >
          <Toggle
            label="Ready all in the Awaken phase"
            checked={a.autoReady}
            onChange={(v) => {
              auto('autoReady', v);
            }}
          />
          <Toggle
            label="Channel runes in the Channel phase"
            checked={a.autoChannel}
            onChange={(v) => {
              auto('autoChannel', v);
            }}
          />
          <Toggle
            label="Draw in the Draw phase"
            checked={a.autoDraw}
            onChange={(v) => {
              auto('autoDraw', v);
            }}
          />
          <Toggle
            label="Heal and expire “this turn” effects at end of turn"
            checked={a.autoHeal}
            onChange={(v) => {
              auto('autoHeal', v);
            }}
          />
          <Toggle
            label="Pre-select the suggested payment"
            hint="One tap confirms it."
            checked={a.autoPaySuggestion}
            onChange={(v) => {
              auto('autoPaySuggestion', v);
            }}
          />
          <Toggle
            label="Trigger reminders"
            hint="“Yasuo: attack trigger · Resolve / Dismiss”"
            checked={a.triggerReminders}
            onChange={(v) => {
              auto('triggerReminders', v);
            }}
          />
          <Toggle
            label="Warnings about illegal-looking actions"
            hint="You can always do it anyway."
            checked={a.warnings}
            onChange={(v) => {
              auto('warnings', v);
            }}
          />
        </Section>
      </div>
    </div>
  );
}
